import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

/**
 * TransferList
 * Classification: custom
 *
 * Two-list picker for moving items between an "available" and "selected" set.
 * Composes MUI List + Checkbox; encapsulates the selection/move logic MUI does
 * not provide.
 */
export interface TransferListProps {
  /** All available item ids/labels. */
  items: string[];
  /** Initially selected (right-side) items. */
  defaultSelected?: string[];
  onChange?: (selected: string[]) => void;
  leftTitle?: string;
  rightTitle?: string;
}

export function TransferList({
  items,
  defaultSelected = [],
  onChange,
  leftTitle = 'Available',
  rightTitle = 'Selected',
}: TransferListProps) {
  const [selected, setSelected] = useState<string[]>(defaultSelected);
  const [checked, setChecked] = useState<string[]>([]);

  const left = useMemo(() => items.filter((i) => !selected.includes(i)), [items, selected]);
  const right = selected;

  const toggleCheck = (item: string) =>
    setChecked((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));

  const commit = (next: string[]) => {
    setSelected(next);
    setChecked([]);
    onChange?.(next);
  };

  const moveRight = () => commit([...selected, ...left.filter((i) => checked.includes(i))]);
  const moveLeft = () => commit(selected.filter((i) => !checked.includes(i)));

  const renderList = (title: string, listItems: string[]) => (
    <Paper variant="outlined" sx={{ width: 200 }}>
      <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0.5 }}>
        {title}
      </Typography>
      <List dense sx={{ height: 200, overflow: 'auto' }}>
        {listItems.map((item) => (
          <ListItemButton key={item} onClick={() => toggleCheck(item)}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox edge="start" size="small" checked={checked.includes(item)} tabIndex={-1} disableRipple />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {renderList(leftTitle, left)}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button size="small" variant="secondary" onClick={moveRight} disabled={!left.some((i) => checked.includes(i))}>
          &gt;
        </Button>
        <Button size="small" variant="secondary" onClick={moveLeft} disabled={!right.some((i) => checked.includes(i))}>
          &lt;
        </Button>
      </Box>
      {renderList(rightTitle, right)}
    </Box>
  );
}
